import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ScrollService } from "@merri/common";
import { Subscription, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import {
  AnalyticsService,
  AppColors,
  BlobService,
  PRODUCT_NAME,
  QueryParamsMap,
  ReferralService,
  SeoService,
  SessionService,
  SessionUser,
  SvgIcon,
  UserInvitationService,
} from "src/app/core";
import { BrandingService } from "src/app/core/services/branding.service";
import { ReferralRewardsService } from "src/app/features/referral-rewards";
import { DisplayCardInfo, HelperMethods, LEARN_MORE_VENUE_LINK } from "src/app/shared";
import { HowItWorksWidgetInfo } from "../homepage-how-it-works-widget/homepage-how-it-works-widget.component";

@Component({
  selector: "app-homepage",
  templateUrl: "./homepage.component.html",
  styleUrls: ["./homepage.component.scss"],
  standalone: false,
})
export class HomepageComponent implements OnInit, OnDestroy, AfterViewInit {
  private sessionUserSub$: Subscription;
  private queryParamsSub$: Subscription;

  SvgIcon = SvgIcon;
  AppColors = AppColors;
  showInvitation: boolean;
  partnerName: string;
  sessionUser: SessionUser;
  topCtaQueryParams: QueryParamsMap;
  topCtaText: string;
  topCtaLink: string;
  homepageImageUrl: string;
  homepageVideoUrl: string;
  howItWorksWidgets: HowItWorksWidgetInfo[];
  otherFeaturesCards: DisplayCardInfo[];
  showVenueJoinCta: boolean;

  readonly LEARN_MORE_VENUE_LINK = LEARN_MORE_VENUE_LINK;
  readonly PRODUCT_NAME = PRODUCT_NAME;

  @ViewChild("ctaJumpTo") ctaJumpTo: ElementRef<HTMLElement>;
  @ViewChild("backgroundContainer") backgroundContainer: ElementRef<HTMLElement>;
  @ViewChild("backgroundImage") backgroundImage: ElementRef<HTMLImageElement>;
  @ViewChild("backgroundVideo") backgroundVideo: ElementRef<HTMLVideoElement>;

  constructor(
    public brandingService: BrandingService,
    private seoService: SeoService,
    private userInvitationService: UserInvitationService,
    private sessionService: SessionService,
    private analyticsService: AnalyticsService,
    private blobService: BlobService,
    private route: ActivatedRoute,
    private scrollService: ScrollService,
    private _referralService: ReferralService,
    private _referralRewardsService: ReferralRewardsService,
  ) {
    this.howItWorksWidgets = this.buildHowItWorksWidgets();
    this.otherFeaturesCards = this.buildOtherFeaturesCards();
  }

  @HostListener("window:resize")
  onResize() {
    this.resizeBackground();
  }

  ngOnInit() {
    this.homepageImageUrl = this.blobService.getHomePageImageUrl();
    this.homepageVideoUrl = this.blobService.getHomePageVideoUrl();

    // maybe not needed since event is triggered by user
    this.sessionUserSub$ = this.sessionService.sessionUser$.subscribe(user => {
      this.sessionUser = user;
      this.setLoggedInState();
    });

    this.queryParamsSub$ = this.route.queryParams
      .pipe(
        switchMap(params => {
          this.setState(null, null, null, true);

          this._referralService.captureCode(params);
          this._referralRewardsService.captureCode(params);

          return this.userInvitationService.validateInvitationCode$(params.i).pipe(
            map(invite => {
              if (invite?.valid) {
                this.setState("Partner Invitation", invite.partnerName, invite.code, false);
                return true;
              }

              return false;
            }),
            catchError(() => of(false)),
          );
        }),
        catchError(() => of(false)),
      )
      .subscribe((partnerInvite: boolean) => {
        if (!partnerInvite) {
          this.setState("Home", null, null, false);
        }
      });
  }

  ngOnDestroy() {
    if (this.queryParamsSub$) this.queryParamsSub$.unsubscribe();
    if (this.sessionUserSub$) this.sessionUserSub$.unsubscribe();

    const video = this.backgroundVideo && this.backgroundVideo.nativeElement;
    if (video) {
      HelperMethods.clearVideoSource(video);
    }
  }

  ngAfterViewInit() {
    const video = this.backgroundVideo && this.backgroundVideo.nativeElement;
    if (video) {
      video.muted = true;
      video.autoplay = true;
      video.loop = true;
    }

    this.resizeBackground();
  }

  onEnterDashboardClick() {
    this.analyticsService.sendLinkClickEvent("Enter Dashboard from Homepage");
  }

  onTopCtaJumperClick() {
    this.scrollToSection(this.ctaJumpTo);
  }

  // make common
  private scrollToSection(element: ElementRef<HTMLElement>): void {
    if (element) {
      const top = element.nativeElement.getBoundingClientRect().top;
      const offset = window.scrollY;

      this.scrollService.scrollWindowTo(0, top + offset);
    }
  }

  private setLoggedInState() {
    if (this.sessionUser) {
      this.topCtaText = this.sessionUser.portalName;
      this.topCtaLink = this.sessionUser.portalLink;
      this.topCtaQueryParams = null;
      this.showVenueJoinCta = false;
    } else {
      this.topCtaText = "Join Us";
      this.topCtaLink = "/signup";
      this.topCtaQueryParams = this.userInvitationService.getSignUpQueryParams();
      this.showVenueJoinCta = true;
    }
  }

  private setState(title: string, partnerName: string, inviteCode: string, validating: boolean) {
    if (title) this.seoService.postFixTitle(title);
    else this.seoService.resetTitle();

    this.partnerName = partnerName;
    this.showInvitation = !!inviteCode && !validating && !this.sessionUser;

    if (!validating && !inviteCode) {
      this.userInvitationService.clear();
    }

    this.topCtaQueryParams = this.sessionUser ? null : this.userInvitationService.getSignUpQueryParams();
  }

  private resizeBackground() {
    const container = this.backgroundContainer && this.backgroundContainer.nativeElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      let width = "auto",
        height = "100%";

      if (rect.width > rect.height) {
        if (rect.width / rect.height > 16 / 9) {
          width = "100%";
          height = "auto";
        }
      }

      const img = this.backgroundImage && this.backgroundImage.nativeElement;
      if (img) {
        img.style.width = width;
        img.style.height = height;
      }

      const vid = this.backgroundVideo && this.backgroundVideo.nativeElement;
      if (vid) {
        vid.style.width = width;
        vid.style.height = height;
      }
    }
  }

  private buildHowItWorksWidgets(): HowItWorksWidgetInfo[] {
    return [
      {
        title: "1. Choose Your Venue",
        blurb:
          "We provide an extensive catalog of venue partners. If your venue isn't listed, you can upload a floor plan and we'll add it for you (for free!)",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-venue.gif"),
      },
      {
        title: "2. Create Your Vision Board",
        blurb:
          "Construct your vision from floor to ceiling by selecting from our collection of  30,000+ furniture rentals, linens, lighting, florals, and decor.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-vb.gif"),
        reversed: true,
      },
      {
        title: "3. Bring It To Life",
        blurb: "Use our drag-and-drop features to design in 3D—the easiest and most efficient way to see your vision come to life.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-design.gif"),
      },
      {
        title: "4. Seamlessly Collaborate",
        blurb: "Optimize how you work together with our built-in collaboration toolkit and export + share options.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-collaborate.gif"),
        reversed: true,
      },
    ];
  }

  private buildOtherFeaturesCards(): DisplayCardInfo[] {
    return [
      {
        title: "TABLESCAPE",
        blurb:
          "Our most coveted design tool, the tablescape feature is as detailed as it gets, letting users tailor place settings down to the smallest, most nuanced detail, like how the napkin is folded.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-tablescape.jpg"),
        backColor: AppColors.AccentPeach,
        foreColor: AppColors.Primary,
      },
      {
        title: "DRAPING",
        blurb:
          "The most transformative option for your event, our draping tool allows users to easily drape an entire space with added customization options to layer and swag different fabrics.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-drapes.jpg"),
        backColor: AppColors.White,
        foreColor: AppColors.Primary,
      },
      {
        title: "TENTING",
        blurb:
          "Our 3D tent designer simplifies the complicated art of tents. Size your tent, place it in your space, and then accessorize using our selection of tent floors, walls, liners, and more.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-tents.jpg"),
        backColor: AppColors.LightBeige,
        foreColor: AppColors.Primary,
      },
      {
        title: "FLORALS",
        blurb: "Upload images of centerpieces, floral installations, and decor to see your full vision come to life instantly.",
        imgSrc: this.blobService.getAssetBlobUrl("how-it-works-florals.jpg"),
        backColor: AppColors.Beige,
        foreColor: AppColors.Primary,
      },
    ];
  }
}
